import { SEO } from '../components/SEO';

export default function TermsPage() {
  return (
    <main className="pt-20" data-testid="terms-page">
      <SEO
        title="Terms of Service"
        description="Terms of Service for siddharthkaul.com. Read the terms governing the use of our website and consulting services."
        canonicalUrl="/terms"
      />
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <h1 className="font-['Montserrat'] font-extrabold text-4xl md:text-5xl text-[#2C3E50] mb-6">
            Terms of Service
          </h1>
          <p className="text-[#64748B] mb-8">
            Last Updated: January 16, 2026
          </p>

          <div className="prose prose-slate max-w-none space-y-8">
            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                1. Agreement to Terms
              </h2>
              <p className="text-[#64748B] leading-relaxed mb-4">
                These Terms of Service ("Terms") govern your use of siddharthkaul.com (the "Site") operated by Siddharth Kaul ("I", "me", "my"). By accessing or using the Site, you agree to be bound by these Terms.
              </p>
              <p className="text-[#64748B] leading-relaxed">
                If you disagree with any part of these Terms, you may not access the Site or use my services.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                2. Use of the Site
              </h2>

              <h3 className="font-['Montserrat'] font-semibold text-xl text-[#2C3E50] mb-3 mt-6">
                2.1 Permitted Use
              </h3>
              <p className="text-[#64748B] leading-relaxed mb-3">
                You may use the Site for lawful purposes only. You agree to use the Site in a manner consistent with all applicable laws and regulations.
              </p>

              <h3 className="font-['Montserrat'] font-semibold text-xl text-[#2C3E50] mb-3 mt-6">
                2.2 Prohibited Conduct
              </h3>
              <p className="text-[#64748B] leading-relaxed mb-3">
                You agree NOT to:
              </p>
              <ul className="list-disc list-inside text-[#64748B] space-y-2 ml-4">
                <li>Use the Site for any unlawful purpose or to violate any laws</li>
                <li>Attempt to gain unauthorized access to any portion of the Site</li>
                <li>Interfere with or disrupt the Site's servers or networks</li>
                <li>Transmit viruses, malware, or any harmful code</li>
                <li>Scrape, crawl, or harvest content without permission</li>
                <li>Impersonate any person or entity</li>
                <li>Spam or send unsolicited communications</li>
              </ul>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                3. Intellectual Property Rights
              </h2>

              <h3 className="font-['Montserrat'] font-semibold text-xl text-[#2C3E50] mb-3 mt-6">
                3.1 Site Content
              </h3>
              <p className="text-[#64748B] leading-relaxed mb-4">
                All content on this Site, including text, graphics, logos, images, articles, whitepapers, and code, is the property of Siddharth Kaul and is protected by copyright, trademark, and other intellectual property laws.
              </p>

              <h3 className="font-['Montserrat'] font-semibold text-xl text-[#2C3E50] mb-3 mt-6">
                3.2 Limited License
              </h3>
              <p className="text-[#64748B] leading-relaxed mb-3">
                You are granted a limited, non-exclusive, non-transferable license to:
              </p>
              <ul className="list-disc list-inside text-[#64748B] space-y-2 ml-4">
                <li>View and download content for personal, non-commercial use</li>
                <li>Download whitepapers and research materials for educational purposes</li>
                <li>Share links to articles with proper attribution</li>
              </ul>
              <p className="text-[#64748B] leading-relaxed mt-3">
                You may NOT reproduce, distribute, modify, create derivative works, publicly display, or commercially exploit any content without my prior written consent.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                4. Consulting Services
              </h2>

              <h3 className="font-['Montserrat'] font-semibold text-xl text-[#2C3E50] mb-3 mt-6">
                4.1 Service Descriptions
              </h3>
              <p className="text-[#64748B] leading-relaxed mb-4">
                Descriptions of consulting services on this Site are for informational purposes only. Actual services provided will be defined in a separate Statement of Work (SOW) or consulting agreement.
              </p>

              <h3 className="font-['Montserrat'] font-semibold text-xl text-[#2C3E50] mb-3 mt-6">
                4.2 No Guarantee of Results
              </h3>
              <p className="text-[#64748B] leading-relaxed mb-4">
                While I strive to deliver high-quality consulting services, I make no guarantees regarding specific outcomes, cost savings, or business results. Performance metrics and case study results are illustrative examples and may not reflect your specific circumstances.
              </p>

              <h3 className="font-['Montserrat'] font-semibold text-xl text-[#2C3E50] mb-3 mt-6">
                4.3 Engagement Terms
              </h3>
              <p className="text-[#64748B] leading-relaxed mb-4">
                Formal consulting engagements are governed by separate written agreements that supersede these Terms with respect to the specific services provided.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                5. Third-Party Services
              </h2>
              <p className="text-[#64748B] leading-relaxed mb-3">
                This Site integrates third-party services including:
              </p>
              <ul className="list-disc list-inside text-[#64748B] space-y-2 ml-4">
                <li><strong>Calendly:</strong> Appointment scheduling</li>
                <li><strong>Google Analytics:</strong> Website analytics</li>
                <li><strong>Vercel:</strong> Hosting and performance monitoring</li>
                <li><strong>External links:</strong> LinkedIn, Substack, etc.</li>
              </ul>
              <p className="text-[#64748B] leading-relaxed mt-3">
                I am not responsible for the content, privacy practices, or terms of service of these third-party services. Your use of third-party services is subject to their respective terms and policies.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                6. User Submissions
              </h2>
              <p className="text-[#64748B] leading-relaxed mb-4">
                When you submit information through contact forms, quote requests, or newsletter signups, you grant me a non-exclusive license to use, reproduce, and store such information for the purposes of providing services and improving the Site.
              </p>
              <p className="text-[#64748B] leading-relaxed">
                You represent and warrant that any information you submit is accurate and does not violate any third-party rights.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                7. Disclaimer of Warranties
              </h2>
              <p className="text-[#64748B] leading-relaxed mb-3">
                THE SITE AND ALL CONTENT ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc list-inside text-[#64748B] space-y-2 ml-4">
                <li>Warranties of merchantability or fitness for a particular purpose</li>
                <li>Warranties that the Site will be uninterrupted, error-free, or secure</li>
                <li>Warranties regarding the accuracy or reliability of content</li>
              </ul>
              <p className="text-[#64748B] leading-relaxed mt-3">
                I do not warrant that the Site will meet your requirements or that defects will be corrected.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                8. Limitation of Liability
              </h2>
              <p className="text-[#64748B] leading-relaxed mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, I SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
              </p>
              <ul className="list-disc list-inside text-[#64748B] space-y-2 ml-4">
                <li>Your use or inability to use the Site</li>
                <li>Any unauthorized access to or use of your data</li>
                <li>Any errors or omissions in any content</li>
                <li>Any conduct or content of any third party on the Site</li>
              </ul>
              <p className="text-[#64748B] leading-relaxed mt-3">
                IN NO EVENT SHALL MY TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID (IF ANY) FOR ACCESSING THE SITE.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                9. Indemnification
              </h2>
              <p className="text-[#64748B] leading-relaxed">
                You agree to indemnify, defend, and hold harmless Siddharth Kaul from any claims, liabilities, damages, losses, and expenses (including legal fees) arising out of or in any way connected with your access to or use of the Site, your violation of these Terms, or your violation of any third-party rights.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                10. Confidentiality and NDAs
              </h2>
              <p className="text-[#64748B] leading-relaxed mb-4">
                Information exchanged through the Site's contact forms is not covered by any confidentiality obligation unless a separate Non-Disclosure Agreement (NDA) is executed.
              </p>
              <p className="text-[#64748B] leading-relaxed">
                For formal consulting engagements, mutual NDAs are standard practice and will be executed before sharing confidential information.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                11. Termination
              </h2>
              <p className="text-[#64748B] leading-relaxed">
                I reserve the right to terminate or suspend your access to the Site at any time, without notice, for conduct that I believe violates these Terms or is harmful to other users, me, or third parties, or for any other reason.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                12. Governing Law and Dispute Resolution
              </h2>

              <h3 className="font-['Montserrat'] font-semibold text-xl text-[#2C3E50] mb-3 mt-6">
                12.1 Governing Law
              </h3>
              <p className="text-[#64748B] leading-relaxed mb-4">
                These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
              </p>

              <h3 className="font-['Montserrat'] font-semibold text-xl text-[#2C3E50] mb-3 mt-6">
                12.2 Dispute Resolution
              </h3>
              <p className="text-[#64748B] leading-relaxed mb-4">
                Any disputes arising out of or relating to these Terms or the Site shall first be resolved through good-faith negotiation. If negotiation fails, disputes shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                13. Modifications to Terms
              </h2>
              <p className="text-[#64748B] leading-relaxed">
                I reserve the right to modify these Terms at any time. Changes will be posted on this page with an updated "Last Updated" date. Your continued use of the Site after changes constitutes acceptance of the revised Terms.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                14. Severability
              </h2>
              <p className="text-[#64748B] leading-relaxed">
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                15. Entire Agreement
              </h2>
              <p className="text-[#64748B] leading-relaxed">
                These Terms constitute the entire agreement between you and me regarding the use of the Site and supersede any prior agreements or understandings, whether written or oral.
              </p>
            </section>

            <section>
              <h2 className="font-['Montserrat'] font-bold text-2xl text-[#2C3E50] mb-4">
                16. Contact Information
              </h2>
              <p className="text-[#64748B] leading-relaxed mb-3">
                If you have questions about these Terms, please contact me:
              </p>
              <div className="bg-[#F8F9FA] rounded-lg p-6">
                <p className="text-[#2C3E50] font-medium">Siddharth Kaul</p>
                <p className="text-[#64748B]">Email: <a href="mailto:contact@siddharthkaul.com" className="text-[#3498DB] hover:underline">contact@siddharthkaul.com</a></p>
                <p className="text-[#64748B]">Website: <a href="https://siddharthkaul.com" className="text-[#3498DB] hover:underline">siddharthkaul.com</a></p>
              </div>
            </section>

            <section className="mt-12 pt-8 border-t border-slate-200 bg-[#F8F9FA] p-6 rounded-lg">
              <h3 className="font-['Montserrat'] font-bold text-lg text-[#2C3E50] mb-3">
                Acknowledgment
              </h3>
              <p className="text-[#64748B] leading-relaxed">
                BY USING THIS SITE, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE.
              </p>
            </section>
          </div>
        </div>
      </section>
    </main>
  );
}
